/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useStoreDispatch } from "../../../store/hooks";
import { setOpenCustomDailog } from "../../../store/helpers/helperReducers";
import { IRfFeed } from "../../../store/api/deviceConfig/types";
import { useRfFeedData } from "./hooks";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";
import { InputField } from "../../Shared/Radix/Inputs/InputField";
import { RadixSelectControl } from "../../ui/radix-select";
import { Label } from "../../ui/label";

interface IAddEditRfFeedProps {
  selectedRfFeed?: IRfFeed;
  modelOpenMode?: string;
}

const AddEditRfFeedLatest: React.FC<IAddEditRfFeedProps> = ({ modelOpenMode = "Add", selectedRfFeed }) => {
  const dispatch = useStoreDispatch();
  const { RfFeedData, setRfFeedData, isAddNewRfFeed, submitHandler, onblureHandler, errMsg, setErrMsg } = useRfFeedData(selectedRfFeed);

  const handleChange = (name: string, value: string) => {
    setRfFeedData({ ...RfFeedData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleClose = () => {
    dispatch(setOpenCustomDailog(false));
  };

  const isEnableSave =
    !!RfFeedData?.index &&
    !!RfFeedData?.type &&
    !!RfFeedData?.dlarfcn &&
    !!RfFeedData?.ularfcn &&
    !!RfFeedData?.dlBandwidth &&
    !!RfFeedData?.ulBandwidth &&
    !(Object.values(errMsg).filter((item) => !!item && item).length > 0);

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        type="submit"
        form="add-rffeed-form"
        disabled={!isEnableSave}
      >
        Save
      </Button>
    </>
  );

  return (
    <Sheet
      open={true}
      onClose={handleClose}
      title={`${modelOpenMode} RF Feed`}
      footer={footer}
      width="520px"
    >
      <form
        id="add-rffeed-form"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          submitHandler();
        }}
      >
        <div className="space-y-5 pt-2">
          <InputField
            label="Index"
            name="index"
            type="number"
            required
            disabled={!isAddNewRfFeed}
            value={RfFeedData?.index ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            min={1}
            max={8}
            placeholder="Enter index"
            autoFocus={true}
          />

          <div className="space-y-2">
            <Label htmlFor="rffeed-type">Type</Label>
            <RadixSelectControl
              options={["RFFeedTypeLTE", "RFFeedTypeNR", "RFFeedTypeUMTS"].map((opt) => ({ value: opt, label: opt }))}
              value={RfFeedData?.type || ""}
              onValueChange={(value) => handleChange("type", value)}
              placeholder="Select type"
            />
          </div>

          <InputField
            label="DLARFCN"
            name="dlarfcn"
            type="number"
            required
            value={RfFeedData?.dlarfcn ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter DLARFCN"
          />

          <InputField
            label="ULARFCN"
            name="ularfcn"
            type="number"
            required
            value={RfFeedData?.ularfcn ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter ULARFCN"
          />

          <InputField
            label="DL Bandwidth"
            name="dlBandwidth"
            type="number"
            required
            value={RfFeedData?.dlBandwidth ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter DL bandwidth"
          />

          <InputField
            label="UL Bandwidth"
            name="ulBandwidth"
            type="number"
            required
            value={RfFeedData?.ulBandwidth ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter UL bandwidth"
          />
        </div>
      </form>
    </Sheet>
  );
};

export default AddEditRfFeedLatest;
